import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Backend is served from the same origin as the front-end on Replit.
// Allow an override via REACT_APP_API_BASE_URL for future deployments.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function SuccessPage() {
  const location = useLocation();
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailSentMessage, setEmailSentMessage] = useState('');

  const CALENDLY_LINK = 'https://calendly.com/nicksolo96/30min';

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const sid = queryParams.get('session_id');
    if (sid) {
      setSessionId(sid);
    } else {
      setError('Checkout session ID not found in URL.');
      setIsLoading(false);
    }
  }, [location]);

  useEffect(() => {
    if (sessionId) {
      const fetchSessionAndSendEmail = async () => {
        setIsLoading(true);
        setError(null);
        setEmailSentMessage('');

        try {
          // Step 1: Fetch checkout session details from your backend
          const sessionDetailsUrl = `${API_BASE_URL}/checkout-session-details?session_id=${sessionId}`;
          console.log(`Fetching from: ${sessionDetailsUrl}`); // Log the URL
          const sessionDetailsResponse = await fetch(sessionDetailsUrl);
          
          // Check if response is ok before trying to parse
          if (!sessionDetailsResponse.ok) {
            // Try to get text first, as it might be HTML
            const errorText = await sessionDetailsResponse.text(); 
            console.error('Server response error:', {
              status: sessionDetailsResponse.status,
              statusText: sessionDetailsResponse.statusText,
              responseText: errorText.substring(0, 200) + '...'
            });
            
            try {
              const errorData = JSON.parse(errorText); // Try to parse as JSON
              throw new Error(errorData.error || `Failed to fetch checkout session details. Status: ${sessionDetailsResponse.status}`);
            } catch (jsonParseError) {
              // If JSON parsing fails, it's likely HTML or plain text
              throw new Error(`Server error (${sessionDetailsResponse.status}): The server may not be running properly. Check console for details.`);
            }
          }

          // Check content type before parsing as JSON
          const contentType = sessionDetailsResponse.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            const responseText = await sessionDetailsResponse.text();
            console.error('Unexpected content type:', contentType);
            console.error('Response text:', responseText.substring(0, 200) + '...');
            throw new Error('Server returned HTML instead of JSON. The backend server may not be running properly.');
          }

          const sessionDetails = await sessionDetailsResponse.json();

          const { customerName, customerEmail, purchaseType, stripeCustomerId } = sessionDetails;

          if (!customerName || !customerEmail || !purchaseType) {
            throw new Error('Required customer details or purchase type missing from session.');
          }
          
          // Store stripeCustomerId in localStorage for the "Manage Subscription" button
          if (stripeCustomerId) {
            localStorage.setItem('stripeCustomerId', stripeCustomerId);
          }

          // Step 2: Send the welcome email
          const sendEmailUrl = `${API_BASE_URL}/send-welcome-email`;
          console.log(`Sending email to: ${sendEmailUrl}`); // Log the URL
          const emailResponse = await fetch(sendEmailUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: customerName,
              email: customerEmail,
              purchaseType: purchaseType,
              stripeCustomerId: stripeCustomerId,
            }),
          });

          if (!emailResponse.ok) {
            const errorText = await emailResponse.text(); // Try to get text first
            try {
              const emailErrorData = JSON.parse(errorText); // Try to parse as JSON
              throw new Error(emailErrorData.error || `Failed to send welcome email. Status: ${emailResponse.status}`);
            } catch (jsonParseError) {
               // If JSON parsing fails, it's likely HTML or plain text
              throw new Error(`Failed to send welcome email. Status: ${emailResponse.status}. Response: ${errorText.substring(0,100)}...`);
            }
          }
          setEmailSentMessage('Welcome email has been sent successfully!');

        } catch (err) {
          console.error('Error object in fetchSessionAndSendEmail:', err);
          console.error('Error name:', err.name);
          console.error('Error message:', err.message);
          console.error('Error stack:', err.stack);
          setError(err.message);
        } finally {
          setIsLoading(false);
        }
      };

      fetchSessionAndSendEmail();
    }
  }, [sessionId]);

  const pageStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '90vh', // Increased height for more content
    textAlign: 'center',
    padding: '40px 20px', // Increased padding
    backgroundColor: '#0A0F1A', // Matching pricing page background
    color: '#E2E8F0',
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
  };

  const messageStyle = {
    fontSize: '2.2rem', // Larger success message
    color: '#68D391', // Brighter green
    marginBottom: '25px',
    fontWeight: 'bold'
  };

  const infoStyle = {
    fontSize: '1.1rem',
    color: '#A0AEC0',
    marginBottom: '20px',
    maxWidth: '600px',
    lineHeight: '1.6'
  };

  const sessionIdStyle = {
    fontSize: '0.9rem',
    color: '#718096',
    marginTop: '10px',
    marginBottom: '30px'
  };

  const actionsContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px', // Space between elements
    marginTop: '20px'
  };

  const buttonStyle = {
    padding: '12px 28px',
    backgroundColor: '#3B82F6', // Consistent with pricing page button
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontSize: '1.05rem',
    fontWeight: '600',
    margin: '5px', // Reduced margin as gap is used in container
    transition: 'background-color 0.3s ease'
  };

  const portalButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#DD6B20' // Orange for portal
  };
  
  const calendlyButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#00A2FF', // Calendly's blue or a distinct blue
    padding: '15px 35px', // Make it more prominent
    fontSize: '1.15rem'
  };

  const nextStepSectionStyle = {
    marginTop: '40px',
    padding: '25px',
    backgroundColor: '#1E293B', // Card background from pricing page
    borderRadius: '12px',
    maxWidth: '650px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 20px 20px -10px rgba(0, 0, 0, 0.1)'
  };

  const nextStepTitleStyle = {
    fontSize: '1.5rem',
    color: '#93C5FD', // Accent color
    marginBottom: '15px',
    fontWeight: 'bold'
  };

  return (
    <div style={pageStyle}>
      <h1 style={messageStyle}>🎉 Payment Successful! 🎉</h1>
      {isLoading && <p style={infoStyle}>Processing your confirmation...</p>}
      {error && <p style={{ ...infoStyle, color: 'red' }}>Error: {error}</p>}
      {emailSentMessage && <p style={{ ...infoStyle, color: '#68D391' }}>{emailSentMessage}</p>}
      
      {!isLoading && !error && (
        <>
          <p style={infoStyle}>
            Thank you for your purchase! Your order has been confirmed. Please check your email for next steps.
          </p>
          {sessionId && <p style={sessionIdStyle}>Checkout Session ID: {sessionId}</p>}
          
          <div style={nextStepSectionStyle}>
            <h2 style={nextStepTitleStyle}>Next Step: Schedule Your Consultation</h2>
            <p style={infoStyle}>
              Please schedule your project consultation call via Calendly. This will be an opportunity for us to discuss your project requirements, website details, and project goals in more detail.
            </p>
            <a 
              href={CALENDLY_LINK} 
              target="_blank" 
              rel="noopener noreferrer" 
              style={calendlyButtonStyle}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#007ACC'} // Darker shade on hover
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#00A2FF'}
            >
              Schedule Your Call Now
            </a>
          </div>

          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'20px', marginTop:'20px' }}>
            <Link to="/" style={buttonStyle}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563EB'} 
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
            >
                Back to Homepage
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

export default SuccessPage; 